import React, { FC, useContext, useState } from 'react'

import { Link } from 'react-router-dom'
import moment from 'moment'
import { ReceiptText, Trash, UsersRound } from 'lucide-react'
import AutorenewIcon from '@mui/icons-material/Autorenew'

import customImageComponent from '../../../../common/CustomImageComponent'
import { Button } from '../../../../ui/button'
import { HeaderInfoEmpty } from '../empty/HeaderInfoEmpty/HeaderInfoEmpty'
import { Badge } from '../../../../ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../ui/tooltip'

import { useChatClient } from '../../../../../pages/messages/components/withStreamClient'
import { useAppSelector } from '../../../../../store/hooks'
import { selectProfileData } from '../../../../../store/user/userSlice'

import { HandleModalContext } from '../../../../../layout/mainlayout/MainLayout'
import { getUserFullName } from '../../../../../helpers/helpers'

import { IUserInterface } from '../../../../../models/users/user.interface'
import {
  HORSE_NAME_DEFAULT,
  IHorseSelected,
  IRegistrationByDayInterface,
} from '../../../../../models/registrations-by-day/registrationByDay.interface'
import { IEventReviewPublish } from '../../../../../models/event-review-publish/event-review-publish.interface'

import { CONST } from '../../../../../const/const'
import { MODAL_CONSTS } from '../../../../../const/modal-const'
import { USE_MODE_CONST } from '../../../../../const/use-mode'

interface RiderHeaderInfoProps {
  eventId: string
  registrarId: string
  registrationId: string
  user: IUserInterface | null
  riders?: IRegistrationByDayInterface[]
  loading: boolean
  activeTab: string
  eventReviewPublish: IEventReviewPublish | null
}

export const RiderHeaderInfo: FC<RiderHeaderInfoProps> = ({
  eventId,
  user,
  riders,
  registrarId,
  registrationId,
  loading,
  activeTab,
  eventReviewPublish,
}) => {
  const handleModalContext = useContext(HandleModalContext)

  const { createChatEvent } = useChatClient()

  const profileData = useAppSelector(selectProfileData)

  const [messageLoading, setMessageLoading] = useState(false)

  const horses: IHorseSelected[] = []
  riders?.forEach((rider) => {
    rider?.horsesSelected?.forEach((item) => {
      const isExist = horses?.find((horse) => horse.name === item.name)
      if (!isExist && item.id !== HORSE_NAME_DEFAULT) horses.push(item)
    })
  })

  const onMessage = async () => {
    if (eventId && user?.id && getUserFullName(user)) {
      try {
        setMessageLoading(true)
        await createChatEvent(
          [
            {
              ...user,
              name: getUserFullName(user),
              role: 'Rider',
              vertical: 'exhibitors',
            },
          ],
          eventId,
          { eventId }
        )

        const url = `${CONST.ROUTES.MANAGE.CLINIC_N_OTHER.URL}/${eventId}/messages`
        window.open(url, '_blank')
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setMessageLoading(false)
      }
    }
  }

  const onShowRegistrarModal = () => {
    handleModalContext?.handleModal(true, MODAL_CONSTS.REGISTRAR_INFO, {
      ...handleModalContext.dataToPassOn,
      eventId,
      registrarId,
      registrationId,
      riderId: user?.id, // we don't use this inside registrar we need "riderId" because rider modal still open
    })
  }

  const onScratch = () => {
    handleModalContext?.handleModal?.(true, MODAL_CONSTS.SCRATCH_FULL_ENTRY, {
      ...handleModalContext?.dataToPassOn,
      userId: user?.id,
      registrationId: registrationId,
      eventId: eventId,
      isRegistrar: false,
      isChargeSalesTax: eventReviewPublish?.EventPaymentSettings?.chargeSalesTax ?? false,
      onSave: () => {
        handleModalContext?.handleModal?.(false, MODAL_CONSTS.RIDER_INFO)
        setTimeout(() => {
          handleModalContext?.handleModal?.(true, MODAL_CONSTS.RIDER_INFO, {
            ...handleModalContext?.dataToPassOn,
          })
        }, 100)
      },
    })
  }

  const allowScratches =
    eventReviewPublish?.EventDetails?.allowScratches || profileData.id === eventReviewPublish?.owner

  if (loading) return <HeaderInfoEmpty />

  return (
    <div className="flex flex-wrap justify-between items-center gap-5">
      <div className="flex gap-5 sm:items-center flex-col sm:flex-row">
        {customImageComponent(
          user?.userProfilePicture,
          user ? getUserFullName(user) : 'N/A',
          'w-16 h-16 min-w-16 border border-black/10 rounded-full'
        )}
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h4 className="font-bold text-xl">{user ? getUserFullName(user) : 'N/A'}</h4>
            <span className="py-1 rounded-full px-4 text-sm bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor">
              Rider
            </span>
            {horses?.map((horse) => (
              <span
                key={horse.id}
                className="py-1 rounded-full px-4 text-sm bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor"
              >
                {horse.name}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap mt-4 gap-y-3 gap-x-5 flex-col sm:flex-row text-black/50">
            <span className="leading-none">{user?.userEmail}</span>
            {user?.userPhoneNumber && typeof user?.userPhoneNumber !== 'object' && (
              <span className="sm:pl-5 sm:border-l sm:border-l-black/20 leading-none">
                {user.userPhoneNumber}
              </span>
            )}
            <span className="sm:pl-5 sm:border-l sm:border-l-black/20 leading-none">
              Registered {moment(riders?.[0]?.created as Date).format('D MMM, YYYY')}
            </span>
          </div>
        </div>
      </div>
      <div className="sm:ml-auto">
        <div className="flex items-center gap-2.5">
          <Tooltip>
            <TooltipTrigger className="w-full">
              <Button
                disabled={!allowScratches}
                variant="outline"
                className="w-10 h-10 rounded-full"
                onClick={onScratch}
              >
                <Trash />
              </Button>
            </TooltipTrigger>
            <TooltipContent hidden={allowScratches}>Disabled by show</TooltipContent>
          </Tooltip>
          <Button
            variant="outline"
            className="w-10 h-10 min-w-10 rounded-full p-1 hover:bg-secondary/80"
            onClick={() => {
              handleModalContext?.handleModal(true, MODAL_CONSTS.PAY_ALL, {
                ...handleModalContext?.dataToPassOn,
                registrationId: registrationId,
                eventId: eventId,
                userId: user?.id,
                registrarId: registrarId,
                defaultOpenedSecondaryAccordion: activeTab,
              })
            }}
          >
            <img src="/assets/og_icons/Banknotes-1.svg" className="w-4" alt="" />
          </Button>
          <Link to={`${CONST.ROUTES.RECEIPT.URL}/${registrationId}`} target="_blank">
            <Badge variant="outline" className="w-10 h-10 rounded-full">
              <ReceiptText />
            </Badge>
          </Link>
          {profileData.userType === USE_MODE_CONST.ORGANIZER && (
            <Button
              variant="outline"
              className="w-10 h-10 rounded-full"
              onClick={onShowRegistrarModal}
            >
              <UsersRound />
            </Button>
          )}
          {profileData.userType === USE_MODE_CONST.ORGANIZER && (
            <Button
              disabled={messageLoading}
              type="button"
              className="rounded-full"
              onClick={onMessage}
            >
              {messageLoading && (
                <AutorenewIcon fontSize="small" className="animate-spin h-4 w-4" />
              )}
              Message
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
